import Dialog from "@mui/material/Dialog";
import {useDispatch, useSelector} from "react-redux";
import {
    FileMeaningsSelectors,
    updateFields
} from "@/features/InControlPanel/ReferenceBooks/LaboratoryClarifications/FileMeanings/Slice/FileMeaningsSlice";
import Toolbar from "@mui/material/Toolbar";
import DialogTitle from "@mui/material/DialogTitle";
import {useTranslation} from "react-i18next";
import {IconButton, useMediaQuery, useTheme} from "@mui/material";
import {CheckCircle, Close} from "@mui/icons-material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import {
    useGetFileMeaningsToExtensionsListQuery
} from "@/api/ReferenceBooks/Lists/LaboratoryClarifications/FileMeaningsToExtensions/List/FileMeaningsToExtensionsListApiSlice";
import {useMemo} from "react";
import {
    useGetActiveFileExtensionsQuery
} from "@/api/ReferenceBooks/Lists/Files/FileExtensions/ListActive/FileExtensionsListApiSlice";
import {GenericAutocomplete} from "@/components/GenericAutocomplete/GenericAutocomplete";
import {useFormik} from "formik";
import Box from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    useCreateMeaningsToExtensionsMutation
} from "@/api/ReferenceBooks/Lists/LaboratoryClarifications/FileMeaningsToExtensions/Create/CreateMeaningsToExtensionsApiSlice";
import {showPopup} from "@/features/PopupMessages/PopupMessages";

function EditFileExtensionsDialog() {
    const {t} = useTranslation();

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch()

    const editFileExtensionsModalVisible = useSelector(FileMeaningsSelectors.editFileExtensionsModalVisible);
    const editFileExtensionsId = useSelector(FileMeaningsSelectors.editFileExtensionsId) ?? "";

    const {
        data: fileMeaningsToExtensionsData,
        isFetching: fileMeaningsToExtensionsIsFetching
    } = useGetFileMeaningsToExtensionsListQuery();
    const fileMeaningsToExtensionsList = useMemo(() => (fileMeaningsToExtensionsData?.data ?? []), [fileMeaningsToExtensionsData?.data])
    const currentFileMeaningsToExtensions = fileMeaningsToExtensionsList.find(item => String(item.id) === String(editFileExtensionsId))

    const dialogTitle = currentFileMeaningsToExtensions?.title ? `${t("Edit")} "${currentFileMeaningsToExtensions.title}"` : t("Edit")

    const {
        data: activeFileExtensionsData,
        isFetching: activeFileExtensionsIsFetching
    } = useGetActiveFileExtensionsQuery();
    const activeFileExtensionsList = useMemo(() => (activeFileExtensionsData?.data ?? []), [activeFileExtensionsData?.data])

    const [createMeaningsToExtensions, {isLoading: isCreatingMeaningsToExtensions}] = useCreateMeaningsToExtensionsMutation();

    const form = useFormik({
        initialValues: {
            id: currentFileMeaningsToExtensions?.id,
            extensions: currentFileMeaningsToExtensions?.extensions ?? [],
        },
        enableReinitialize: true,
        onSubmit: async (values) => {
            const payload = {
                id: String(values.id),
                extensions: values.extensions.map((extension) => extension.id),
            }

            try {
                await createMeaningsToExtensions(payload).unwrap()

                dispatch(updateFields({
                    editFileExtensionsModalVisible: false,
                    editFileExtensionsId: undefined,
                }))

                showPopup({
                    message: t("The request performed successfully."),
                    type: "success",
                    autoClose: 5000,
                })
            } catch (e) {
                showPopup({
                    message: t("An error occured during the request."),
                    type: "error",
                    autoClose: 15000,
                })
            }
        }
    })

    const isLoading = fileMeaningsToExtensionsIsFetching || activeFileExtensionsIsFetching || isCreatingMeaningsToExtensions

    return (
      <Dialog open={editFileExtensionsModalVisible} fullScreen={fullScreen} onClose={handleClose}>
          <Toolbar sx={{display: "flex", justifyContent: "space-between"}}>
              <DialogTitle sx={{flex: 1}}>{dialogTitle}</DialogTitle>

              <IconButton onClick={handleClose}>
                  <Close/>
              </IconButton>
          </Toolbar>

          <form style={{display: "contents"}} onSubmit={form.handleSubmit}>
              <DialogContent>
                  <GenericAutocomplete
                    onChange={(value) => {
                        form.setFieldValue("extensions", value)
                    }}
                    options={activeFileExtensionsList}
                    value={form.values.extensions}
                    getOptionLabel={(option) => option?.title ?? ""}
                    renderOption={(props, option) => {
                      const {key, ...optionProps} = props as typeof props & {key: string | number};

                      return (
                        <li key={key} {...optionProps}>
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%"
                            }}>
                                {option.title}

                                {isChecked(option.id) && (
                                  <CheckCircle color="success"/>
                                )}
                            </Box>
                        </li>
                      );
                    }}
                    multiple
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    loading={isLoading}
                    disableCloseOnSelect
                  />
              </DialogContent>

              <DialogActions>
                  <Button variant="outlined" onClick={handleClose}>{t("Cancel")}</Button>
                  <LoadingButton
                    variant="contained"
                    type="submit"
                    loading={isLoading}
                    disabled={isLoading}
                  >
                      {t("Save")}
                  </LoadingButton>
              </DialogActions>
          </form>
      </Dialog>
    );

    function isChecked(id: string) {
        return form.values.extensions.some((item) => item.id === id)
    }

    function handleClose() {
        dispatch(updateFields({
            editFileExtensionsModalVisible: false,
            editFileExtensionsId: undefined,
        }))
    }
}

export default EditFileExtensionsDialog;
